import * as React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
  SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

interface TimeOption {
  timeslotid: string | number;
  timeslotname: string;
  timeslotcapacity: number;
  timeslotdate: string;
  timeslottimestart: string;
}

interface TimeGroup {
  date: string;
  timeOptions: TimeOption[];
}

interface SelectOptionGroupProps {
  timeOptions: TimeGroup[];
  setFieldValue: (field: string, value: any) => void;
  name: string;
  value?: string | number;
}

const blue = {
  100: '#DAECFF',
  200: '#99CCF3',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  900: '#003A75',
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 320,
  '& .MuiInputLabel-root': {
    fontFamily: '"IBM Plex Sans", sans-serif',
    fontSize: '0.875rem',
    color: theme.palette.mode === 'dark' ? grey[300] : grey[900],
    '&.Mui-focused': {
      color: blue[400],
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  minWidth: 320,
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    background: theme.palette.mode === 'dark' ? grey[900] : '#fff',
    border: `1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]}`,
    color: theme.palette.mode === 'dark' ? grey[300] : grey[900],
    boxShadow: theme.palette.mode === 'dark' 
      ? '0px 2px 6px rgba(0,0,0, 0.50)' 
      : '0px 2px 6px rgba(0,0,0, 0.05)',
    transition: 'all 120ms cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      background: theme.palette.mode === 'dark' ? grey[800] : grey[50],
      borderColor: theme.palette.mode === 'dark' ? grey[600] : grey[300],
    },
    
    '&.Mui-focused': {
      borderColor: blue[400],
      boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? blue[600] : blue[200]}`,
    },
  },
  
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  
  '& .MuiSelect-select': {
    padding: '8px 12px',
    paddingRight: '32px !important',
    lineHeight: 1.5,
    textAlign: 'left',
  },
  
  '& .MuiSelect-icon': {
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '1rem',
    color: theme.palette.mode === 'dark' ? grey[300] : grey[900],
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  padding: '8px 20px',
  borderRadius: 8,
  margin: '2px 6px',
  cursor: 'default',
  
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? grey[800] : grey[100],
    color: theme.palette.mode === 'dark' ? grey[300] : grey[900],
  },
  
  '&.Mui-selected': {
    backgroundColor: `${theme.palette.mode === 'dark' ? blue[900] : blue[100]} !important`,
    color: theme.palette.mode === 'dark' ? blue[100] : blue[900],
    
    '&:hover': {
      backgroundColor: `${theme.palette.mode === 'dark' ? blue[900] : blue[100]} !important`,
    },
  },
  
  '&.Mui-disabled': {
    color: `${theme.palette.mode === 'dark' ? grey[700] : grey[400]} !important`,
    opacity: 1,
  },
  
  '&:focus-visible': {
    outline: `3px solid ${theme.palette.mode === 'dark' ? blue[600] : blue[200]}`,
  },
}));

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.75em',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05rem',
  color: grey[600],
  padding: '15px 0 5px 10px',
  backgroundColor: 'transparent',
  lineHeight: 1,
}));

const StyledMenuProps = {
  PaperProps: {
    sx: (theme: any) => ({
      fontFamily: '"IBM Plex Sans", sans-serif',
      fontSize: '0.875rem',
      boxSizing: 'border-box',
      padding: '6px',
      margin: '12px 0',
      minWidth: 320,
      borderRadius: '12px',
      background: theme.palette.mode === 'dark' ? grey[900] : '#fff',
      border: `1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]}`,
      color: theme.palette.mode === 'dark' ? grey[300] : grey[900],
      boxShadow: theme.palette.mode === 'dark' 
        ? '0px 2px 6px rgba(0,0,0, 0.50)' 
        : '0px 2px 6px rgba(0,0,0, 0.05)',
      maxHeight: 400,
      overflowY: 'auto',
    }),
  },
  MenuListProps: {
    sx: {
      padding: 0,
    },
  },
};

export default function SelectOptionGroup({
  timeOptions,
  setFieldValue,
  name,
  value = ''
}: SelectOptionGroupProps) {
  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string | number;
    
    // Find the selected time option to get additional data
    const selectedOption = timeOptions
      .flatMap(group => group.timeOptions)
      .find(option => option.timeslotid === selectedValue);
    
    if (selectedOption) {
      setFieldValue(name, selectedOption.timeslotid);
      setFieldValue(`${name}-desireddate`, selectedOption.timeslotdate);
      setFieldValue(`${name}-desiredtime`, selectedOption.timeslottimestart);
    }
  };

  return (
    <StyledFormControl>
      <InputLabel 
        id={`${name}-label`}
        shrink={false}
        sx={{ 
          transform: 'none',
          position: 'static',
          marginBottom: 1,
          fontSize: '0.875rem',
          fontWeight: 500,
        }}
      >
        {!value && 'Choose a time'}
      </InputLabel>
      <StyledSelect
        labelId={`${name}-label`}
        value={value}
        onChange={handleChange}
        displayEmpty
        IconComponent={UnfoldMoreRoundedIcon}
        MenuProps={StyledMenuProps}
        renderValue={(selected: any) => {
          if (!selected) {
            return <span style={{ color: grey[500] }}>Choose a time</span>;
          }
          
          const selectedOption = timeOptions
            .flatMap(group => group.timeOptions)
            .find(option => option.timeslotid === selected);
            
          return selectedOption 
            ? `${selectedOption.timeslotname} - ${selectedOption.timeslotcapacity > 0 ? selectedOption.timeslotcapacity : 'Sold out'}`
            : selected;
        }}
      >
        {timeOptions.map((timeGroup) => [
          <StyledListSubheader key={`header-${timeGroup.date}`} disableSticky>
            {timeGroup.date}
          </StyledListSubheader>,
          ...timeGroup.timeOptions.map((option) => (
            <StyledMenuItem
              key={option.timeslotid}
              value={option.timeslotid}
              disabled={option.timeslotcapacity <= 0}
            >
              {option.timeslotname} - {
                option.timeslotcapacity > 0 
                  ? option.timeslotcapacity 
                  : 'Sold out'
              }
            </StyledMenuItem>
          ))
        ])}
      </StyledSelect>
    </StyledFormControl>
  );
}